import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { APICore } from "./api/apiCore";
import { permissionsRefreshed } from "../redux/auth/actions";

/**
 * Re-read the current user's permissions from the server.
 *
 * The portal used to learn permissions exactly once, from the login response:
 * `getLoggedInUser()` reads sessionStorage, the auth reducer seeds its initial state
 * from that, and nothing ever refetched. So a permission granted to a role after a
 * session started never reached that session — the gated control stayed missing, and a
 * reload did not help because it re-read the same sessionStorage. Only a fresh login did.
 *
 * `GET /api/auth/me` returns the live list under `permissions` (plural) where login
 * returns `permission` (singular); the two are the same shape, a flat array of keys.
 *
 * Failure is silent by design: if the call fails the session keeps the permissions it
 * already had, which is exactly the old behaviour. This must never be able to lock
 * someone out of a portal they can otherwise use.
 *
 * Once per app LOAD was not enough. A grant made while the tab is open reached the UI
 * only after a full reload, which is what a user sees as "the button appears after a
 * hard refresh" — client-side navigation never refetched. So we also re-read on route
 * change and when the tab regains focus (the moment someone comes back after being
 * told "try now"), throttled so clicking around a portal cannot turn into a burst of
 * identical calls. An unchanged list still dispatches nothing.
 */
const MIN_INTERVAL_MS = 15_000;
export const usePermissionRefresh = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const lastRunRef = useRef(0);
  const cancelledRef = useRef(false);

  const refresh = useCallback(
    async (force = false) => {
      // Never on the sign-in pages: a leftover session there would only earn a 401.
      if (window.location.pathname.startsWith("/auth/")) return;
      const api = new APICore();
      if (!api.isUserAuthenticated()) return;
      const now = Date.now();
      if (!force && now - lastRunRef.current < MIN_INTERVAL_MS) return;
      lastRunRef.current = now;

      const cancelled = false;
      try {
        const res: any = await api.get("/api/auth/me", {});
        const fresh = res?.data?.data?.permissions ?? res?.data?.permissions;
        if (cancelled || cancelledRef.current || !Array.isArray(fresh)) return;

        const current: string[] = api.getLoggedInUser()?.permission ?? [];
        // Only touch anything when the set actually differs — an unchanged list must not
        // mint a new array reference, or every boot would re-render the whole tree.
        const same =
          current.length === fresh.length && fresh.every((p: string) => current.includes(p));
        if (same) return;

        // sessionStorage and redux both have to move: PrivateRoute's route-level gate
        // reads the stored user, while CAN() reads the store.
        const user = api.getLoggedInUser();
        if (user) api.setLoggedInUser({ ...user, permission: fresh });
        dispatch(permissionsRefreshed(fresh));
      } catch {
        // Keep the permissions we already have.
      }
    },
    [dispatch]
  );

  // App load and every route change. The throttle keeps a click-through of five pages
  // to a single call.
  useEffect(() => {
    cancelledRef.current = false;
    refresh();
    return () => {
      cancelledRef.current = true;
    };
  }, [refresh, pathname]);

  // Coming back to the tab: the likeliest moment for "we just granted it, try now".
  // Forced past the throttle because the user is waiting on exactly this.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") refresh(true);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);
};

export default usePermissionRefresh;
